import React, { useState, useEffect }from "react";
import { Box, MenuItem, Select, TextField, Button, useTheme, Card, Autocomplete, InputLabel, FormControl, useMediaQuery, Tooltip, Alert, Modal, IconButton, Paper, Grid, Typography } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';

import { useDispatch, useSelector } from "react-redux";
import GoogleMapsAutocomplete from "../Navbar/GoogleMapsAutocomplete";
import { setPersonModalOpen, setSnackbarMessage, setSnackbarOpen } from "../../../redux/slices/shared/sharedSlice";
import { updateProfilePicture, useGetPhysicianLocationQuery, useGetPhysicianPreferredInsuranceQuery, useUpdatePhysicianPreferredInsuranceMutation } from "../../../redux/slices/physician/physicianThunk";
import { setLocations, setPersonInfo, setPreferredInsurance } from "../../../redux/slices/physician/physicianSlice";
import { useUpdatePersonInfoMutation } from "../../../redux/slices/physician/physicianThunk";

import { LazyLoadImage } from 'react-lazy-load-image-component';
import BlankProfilePicture from '../../../assets/blank_profile_picture.png';
import '../../../css/PfpStyles.css'
import 'react-lazy-load-image-component/src/effects/blur.css';
import PersonCard from "./PersonCard";
import MaskedInput from 'react-text-mask';

const PersonInfoForm = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const onMobile = !useMediaQuery('(min-width:600px)');

    const { personInfo, locations: docLocationsState, preferredInsurance: preferredInsuranceState, } = useSelector(state => state.physician);
    const { specialtyOptions, insuranceOptions, avatarUploadLoading, personModalOpen } = useSelector(state => state.shared);
    
    const [updatePersonInfo, { error: updatePersonInfoError }] = useUpdatePersonInfoMutation();
    const [updatePhysicianPreferredInsurance, 
        {error: insuranceUpdateError, isLoading: insuranceUpdateLoading}] = useUpdatePhysicianPreferredInsuranceMutation();

    const { data: docLocations, error: locationError, isLoading: docLocationLoading } = useGetPhysicianLocationQuery();
    const { data: preferredInsurance, error: fetchInsuranceError, isLoading: fetchInsuranceLoading} = useGetPhysicianPreferredInsuranceQuery();

    const [subSpecialtyInputFocussed, setSubSpecialtyInputFocussed] = useState(false);
    const [changeTimer, setChangeTimer] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalType, setModalType] = useState("");

    const modalStyle = onMobile ?{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        height: "",
        flexDirection: 'column',
        justifyContent: '',
        alignItems: 'center',
        width: "100vw",
        minHeight: "90%",
        maxWidth: "100%",
        maxHeight: "85vh",
        minWidth: "95%",
        bgcolor: 'white',
        borderRadius: '0rem',
        boxShadow: 24,
        px: 2,
        pb: 3,
        overflowY: !onMobile ? "" : "scroll",
    } : {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        height: "90%",
        width: "90%",
        maxWidth: '40rem',
        maxHeight: '35rem',
        bgcolor: 'white',
        borderRadius: !onMobile ? '1rem' : '0rem',
        boxShadow: 24,
        pt: 2,
        px: 3,
        pb: 3,
        overflowY: !onMobile ? "" : "scroll",
    };

    const buttonStyles = {
        backgroundColor: theme.palette.caralystGreen.main,
        color: "white",
        width: "32%",
        textTransform: 'none',
        height: "2.25rem",
        //add more shadow to the button
        boxShadow: '0px 2px 4px -1px rgba(0,0,0,0.5)',
        fontWeight: "600",
        "&:hover": {
            backgroundColor: theme.palette.caralystGreen.main,
        }
    }

    const getPfpProps = () => {
        const style = { width: '100%', borderRadius: "0.5rem" }
        if (personInfo && personInfo.Files && personInfo.Files.length && personInfo.Files[0].url)
            return {
                style,
                src: personInfo.Files[0].url,
                alt: "doctor",
                effect: 'blur'
            }
        if(personInfo?.avatar)
            return {
                style,
                src: personInfo.avatar,
                alt: "doctor",
                effect: 'blur'
            }
        return {
            style,
            src: BlankProfilePicture,
            alt: "blank profile",
            effect: 'blur'
        }
    }

    const handleSubSpecialtyInputFocus = (e) => {
        e.preventDefault();
        setSubSpecialtyInputFocussed(true);
    }

    const handleSubSpecialtyInputBlur = (e) => {
        e.preventDefault();
        setSubSpecialtyInputFocussed(false);
    }

    const handleCloseModal = () => {
        setModalOpen(false); 
    }

    const handleOpenModal = (e, type) => {
        e.preventDefault();
        setModalType(type);
        setModalOpen(true);
    }

    const handleChangePersonInfo = async (e, type) => {
        let newPersonInfo;
        clearTimeout(changeTimer);
        const timerId = setTimeout(async () => {
            switch(type) {
                case "first_name":
                    newPersonInfo = {
                        ...personInfo,
                        first_name: e.target.value,
                    }
                    break;
                case "last_name":
                    newPersonInfo = {
                        ...personInfo,
                        last_name: e.target.value,
                    }
                    break;
                case "user_qualification":
                    newPersonInfo = {
                        ...personInfo,
                        user_qualification: e.target.value,
                    }
                    break;
                case "bio":
                    newPersonInfo = {
                        ...personInfo,
                        bio: e.target.value,
                    }
                    break;
                case "education":
                    newPersonInfo = {
                        ...personInfo,
                        education: e.target.value,
                    }
                    break;
                case "residency":
                    newPersonInfo = {
                        ...personInfo,
                        residency: e.target.value,
                    }
                    break;
                case "phone":
                    newPersonInfo = {
                        ...personInfo,
                        phone: e.target.value,
                    }
                    break;
                case "modality":
                    newPersonInfo = {
                        ...personInfo,
                        modality: e.target.value,
                    }
                    break;
                case "appointment_url":
                    newPersonInfo = {
                        ...personInfo,
                        site: e.target.value,
                    }
                    break;
                case "user_specialty":
                    newPersonInfo = {
                        ...personInfo,
                        user_specialty: e.target.value,
                    }
                    break;
                case "sub_specialty":
                    newPersonInfo = {
                        ...personInfo,
                        sub_specialty: e.target.value,
                    }
                    break;
                case "avatar":
                    dispatch(updateProfilePicture({image: e.target.files[0], personInfo}));
                default:
                    break;
            }
            if(newPersonInfo) {
                await updatePersonInfo({newPersonInfo}).unwrap();
                dispatch(setPersonInfo(newPersonInfo));
            }
        }, 500);
        setChangeTimer(timerId);
    }

    const handleChangeInsurance = async (e, insurances) => {
        e.preventDefault();
        console.log(insurances)
        if(insurances.length === 0) {
            dispatch(setPreferredInsurance([]));
            await updatePhysicianPreferredInsurance({insurances: []}).unwrap();
        }else if(!insurances) {
            dispatch(setSnackbarMessage("Insurances input is invalid. Please try again."))
            dispatch(setSnackbarOpen(true));
        } else if(insurances.length > 0) {
            await updatePhysicianPreferredInsurance({insurances}).unwrap();
            dispatch(setPreferredInsurance(insurances));
        }
    }

    const handlePasteInsurance = async (e) => {
        const pastedText = e.clipboardData.getData('Text');
        const newInsurances = pastedText.split(',').map((value) => value.trim());
    
        // Remove any empty values
        const nonEmptyInsurances = newInsurances.filter((value) => value !== '');
        const updatedInsurances = [...preferredInsurance, ...nonEmptyInsurances];

        dispatch(setPreferredInsurance(updatedInsurances));
        await updatePhysicianPreferredInsurance({insurances: updatedInsurances}).unwrap();
        e.preventDefault();
    };

    useEffect(() => {
        if (docLocations)
            dispatch(setLocations(docLocations));
        if (preferredInsurance)
            dispatch(setPreferredInsurance(preferredInsurance));
    }, [docLocations, avatarUploadLoading, preferredInsurance, dispatch]);

    const formSpacing = "12px";
    const doubleRowStyles = {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        marginBottom: formSpacing
    };

    const labelProps = {
        variant: "h5",
        fontWeight: "bold",
        sx: { marginBottom: "0.5rem" }
    }

    return (
        <>
            <Box sx={{
                display: (updatePersonInfoError) ? "block" : "none",
                width: "100%",
                maxWidth: "50rem",
                padding: "10px",
            }}>
                <Alert severity="error">
                    There was an error updating your info, try reloading the page and trying again.
                </Alert>
            </Box>
            <Box sx={{
                display: "flex", 
                flexDirection: onMobile ? "column" : "row",
                alignItems: onMobile ? "center" : "flex-start",
                justifyContent: "center",
                marginTop: "5rem"
            }}>
                <Tooltip title="Click to change profile picture" placement="top">
                    <Button
                        component="label"
                        className="pfp-button"
                        sx={{ width: "25%", maxWidth: "20rem", minWidth: "15rem", height: "auto", display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                    >
                        <LazyLoadImage
                            {...getPfpProps()}
                            className="pfp"
                            sx={{ borderRadius: "0.5rem", width: '100%', height: 'auto' }}
                        />
                        <Typography
                            variant="caption"
                            sx={{ marginTop: '0.5rem', color: 'text.secondary', width: "120%", textAlign: "center" }}
                        >
                            Click to change profile picture
                        </Typography>
                        <input
                            type="file"
                            hidden
                            accept="image/png, image/jpeg, image/jpg"
                            onChange={async (e) => await handleChangePersonInfo(e, "avatar")}
                        />
                    </Button>
                </Tooltip>
                <Box sx={{width: "45%", minWidth: "25rem", maxWidth: "70rem", margin: onMobile ? "1rem 0 4rem 0rem" : "0.25rem 0 4rem 0.5rem" }}>
                    <Typography {...labelProps}>
                        Personal Information
                    </Typography>
                    <Box sx={doubleRowStyles}>
                        <TextField
                            variant="filled"
                            label="First Name"
                            onChange={async (e) => await handleChangePersonInfo(e, "first_name")}
                            defaultValue={personInfo?.first_name ? personInfo.first_name : ""}
                            sx={{ width: "49%" }}
                        />
                        <TextField
                            variant="filled"
                            label="Last Name"
                            onChange={async (e) => await handleChangePersonInfo(e, "last_name")}
                            defaultValue={personInfo?.last_name ? personInfo.last_name : ""}
                            sx={{ width: "49%" }}
                        />
                    </Box>
                    <Box sx={doubleRowStyles}>
                        <TextField
                            variant="filled"
                            label="Phone"
                            type="tel"
                            onChange={async (e) => await handleChangePersonInfo(e, "phone")}
                            defaultValue={personInfo?.phone ? personInfo.phone : ""}
                            sx={{ width: "49%" }}
                            InputProps={{
                                inputComponent: MaskedInput,
                                inputProps: {
                                    mask: ['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/],
                                    className: "form-control",
                                    placeholder: "Enter a phone number",
                                    guide: false,
                                    id: "my-input-id",
                                },
                            }}
                        />
                        <TextField
                            variant="filled"
                            label="Qualifications"
                            onChange={async (e) => await handleChangePersonInfo(e, "user_qualification")}
                            defaultValue={personInfo?.user_qualification ? personInfo.user_qualification : ""}
                            sx={{ width: "49%" }}
                        />
                    </Box>
                    <TextField
                        variant="filled"
                        label="Bio"
                        multiline
                        rows={5}
                        onChange={async (e) => await handleChangePersonInfo(e, "bio")}
                        defaultValue={personInfo?.bio ? personInfo.bio : ""}
                        sx={{ width: "100%", minHeight: "4rem", marginBottom: formSpacing }}
                    />
                    <Typography {...labelProps}>
                        Professional Information
                    </Typography>
                    <Box sx={doubleRowStyles}>
                        <FormControl sx={{ width: "49%" }}>
                            <InputLabel id="select-helper-label-1">
                                Specialty
                            </InputLabel>
                            <Select
                                variant="filled"
                                label="Specialty"
                                onChange={async (e) => await handleChangePersonInfo(e, "user_specialty")}
                                defaultValue={personInfo?.user_specialty ? personInfo.user_specialty : ""}
                            >
                                {["emptySpace", ...specialtyOptions].map((specialty) => (
                                    specialty === "emptySpace" ?
                                        <MenuItem key={specialty} sx={{color: "rgba(0,0,0,0)"}} value="">{"test"}</MenuItem>
                                        :
                                        <MenuItem key={specialty} value={specialty}>{specialty}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl sx={{ width: "49%" }}>
                            <InputLabel 
                                id="select-helper-label-2" 
                                shrink={subSpecialtyInputFocussed || personInfo.sub_specialty !== ""}
                            >
                                Subspecialty
                            </InputLabel>
                            <TextField
                                // InputLabelProps={{shrink: true}}
                                variant="filled"
                                // label="Subspecialty"
                                onChange={async (e) => await handleChangePersonInfo(e, "sub_specialty")}
                                onFocus={async (e) => await handleSubSpecialtyInputFocus(e)}
                                onBlur={async (e) => await handleSubSpecialtyInputBlur(e)}
                                defaultValue={personInfo?.sub_specialty ? personInfo.sub_specialty : ""}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={doubleRowStyles}>
                        <TextField
                            variant="filled"
                            label="Education"
                            onChange={async(e) => await handleChangePersonInfo(e, "education")}
                            defaultValue={personInfo?.education ? personInfo.education : ""}
                            sx={{ width: "49%" }}
                        />
                        <TextField
                            variant="filled"
                            label="Residency"
                            onChange={async (e) => await handleChangePersonInfo(e, "residency")}
                            defaultValue={personInfo?.residency ? personInfo.residency : ""}
                            sx={{ width: "49%" }}
                        />
                    </Box>
                    <FormControl variant="filled" sx={{ width: "100%", marginBottom: formSpacing }}>
                        <InputLabel>Modality</InputLabel>
                        <Select
                            onChange={async (e) => await handleChangePersonInfo(e, "modality")}
                            defaultValue={personInfo?.modality ? personInfo.modality : ""}
                        >
                            <MenuItem sx={{color: "rgba(0,0,0,0)"}}value="">{"test"}</MenuItem>
                            <MenuItem value="In person and virtual appointments available">In person and virtual appointments available</MenuItem>
                            <MenuItem value="In person appointments only">In person appointments only</MenuItem>
                            <MenuItem value="Virtual appointments only">Virtual appointments only</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        variant="filled"
                        label="Appointment URL"
                        onChange={async (e) => await handleChangePersonInfo(e, "appointment_url")}
                        defaultValue={personInfo?.site ? personInfo.site : ""}
                        sx={{ width: "100%", marginBottom: formSpacing}}
                    />
                    <Box sx={{display: "flex", flexDirection: "row", justifyContent: "space-between"}}>
                        <Button 
                            variant="raised"
                            onClick={e => handleOpenModal(e, "insurance")}
                            disabled={fetchInsuranceLoading}
                            sx={{...buttonStyles, gridColumn: '3', gridRow: '5'}}
                        >
                            {preferredInsuranceState?.length > 0 ?
                                preferredInsuranceState?.length === 1 ?
                                    `${preferredInsuranceState.length} Insurance`
                                    :
                                    `${preferredInsuranceState.length} Insurances`
                                :
                                "Add Insurance"
                            }
                        </Button>
                        <Button 
                            variant="raised"
                            onClick={e => handleOpenModal(e, "location")} 
                            disabled={docLocationLoading}
                            sx={{...buttonStyles, gridColumn: '4', gridRow: '5'}}
                        >
                            {docLocationsState?.length > 0 ? 
                                docLocationsState?.length === 1 ? 
                                    `${docLocationsState.length} Location` 
                                    : 
                                    `${docLocationsState.length} Locations` 
                                : 
                                "Add Location"
                            }
                        </Button>
                        <Button onClick={() => dispatch(setPersonModalOpen(true))} sx={{...buttonStyles,  gridColumn: '3', gridRow: '6'}}>
                            Preview Profile
                        </Button>
                    </Box>
                </Box>
            </Box>
            <Modal open={personModalOpen} onClose={() => dispatch(setPersonModalOpen(false))}>
                <PersonCard />
            </Modal>
            <Modal open={modalOpen} onClose={handleCloseModal}>
                <Card sx={modalStyle}>
                    <>
                        {modalType === "insurance" ?
                            <Tooltip title="Add multiple insurances by pasting in a comma separated list" placement="top">
                                <Autocomplete
                                    id="insurance-autocomplete"
                                    options={insuranceOptions ? insuranceOptions : []}
                                    multiple
                                    defaultValue={preferredInsurance ? preferredInsurance : []}
                                    renderInput={(params) => <TextField 
                                        {...params}
                                        label="Insurance"
                                        onPaste={async (e) => await handlePasteInsurance(e)}
                                        sx={{ maxHeight: "30rem", 
                                            "& .MuiOutlinedInput-notchedOutline": {
                                                border: "none", // Removes the outer border
                                            },
                                            "& .MuiOutlinedInput-root": {
                                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                                    border: "none", // Ensures no border on hover
                                                },
                                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                    border: "none", // Ensures no border when focused
                                                },
                                            },
                                        }}
                                    />}
                                    onChange={async (e, newInsurance) => await handleChangeInsurance(e, newInsurance)}
                                    sx={{ 
                                        width: '80%',
                                        border: "1px solid #D9D9D9",
                                        borderRadius: "0.5rem",
                                        "& .MuiInputBase-root": {
                                            overflow: "auto",
                                            border: "none"
                                        },
                                        "& .MuiAutocomplete-endAdornment": {
                                            display: "none",
                                        },
                                        "& .MuiFilledInput-root:before": {
                                            border: "none",
                                        },
                                        "& .MuiFilledInput-root:after": {
                                            border: "none",
                                        },
                                }}/>
                            </Tooltip>
                            :
                            <GoogleMapsAutocomplete />
                        } 
                        <IconButton onClick={handleCloseModal} sx={{
                            position: "absolute",
                            top: onMobile ? "0.3rem" : "0.5rem",
                            right: onMobile ? "0.3rem" : "0.5rem",
                            rotate: "90deg",
                            width: "25px",
                            height: "25px",
                            backgroundColor: '#D9D9D9', // Set your desired background color
                            borderRadius: '50%', // Optional: Makes the button circular
                            padding: onMobile ? '5px' : '8px'
                        }}>
                            <CloseIcon sx={{ fontSize: 16 }}/>
                        </IconButton>
                    </>
                </Card>
            </Modal>
        </>
    );
}

export default PersonInfoForm;